import { Css, HasIdAndName, SubmitButton, useSnackbar } from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import omit from "lodash/omit";
import { useMemo } from "react";
import { useHistory } from "react-router";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import type { NamedFragment } from "src/fragments/commonFragments.generated";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import { maybeMaterialLeadTimeConfig, maybeOptionPricingConfig } from "src/routes/admin/utils";
import { PageHeader } from "src/routes/layout/PageHeader";
import { useSaveMaterialListingMutation } from "src/routes/libraries/material-catalog/components/material-super-drawer/MaterialSuperDrawer.generated";
import {
  MaterialCatalogDocument,
  MaterialCatalog_ItemFragment,
  MaterialCatalog_MaterialBrandFragment,
  useMaterialCatalogMetadataQuery,
} from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import { createMaterialCatalogUrl, createMaterialDetailsUrl } from "src/RouteUrls";
import { queryResult } from "src/utils";
import { MaterialListingForm } from "./components/material-listing-form/MaterialListingForm";
import { materialListingFormConfig } from "./components/material-super-drawer/MaterialSuperDrawerContext";

/**
 * The "Add Material" page in the Material Catalog.
 *
 * Editing is done via a super-drawer, but we share `MaterialListingForm`s.
 */
export function MaterialPage() {
  const query = useMaterialCatalogMetadataQuery();
  return queryResult(query, ({ items, materialBrands, markets, materialVariantTags }) => (
    <MaterialEditor items={items} brands={materialBrands} markets={markets} tags={materialVariantTags} />
  ));
}

type MaterialEditorProps = {
  items: MaterialCatalog_ItemFragment[];
  brands: MaterialCatalog_MaterialBrandFragment[];
  markets: HasIdAndName[];
  tags: NamedFragment[];
};

function MaterialEditor({ items, brands, markets, tags }: MaterialEditorProps) {
  const [saveMaterialListing, { loading }] = useSaveMaterialListingMutation();
  const { triggerNotice } = useSnackbar();
  const history = useHistory();
  const formConfig = useMemo(() => materialListingFormConfig({ canEditItem: true, canEditType: true }), []);
  const formState = useFormState({
    config: formConfig,
    init: {
      input: { variants: [{}], materialLeadTimeConfig: {}, optionPricingConfig: {} },
      onlyOnce: true,
      map: (input) => input,
    },
    loading,
  });
  const isOptionCatalogV2Enabled = useFeatureFlag(FeatureFlagType.OptionsCatalogV2);

  const handleSave = async () => {
    const { optionPricingConfig, materialLeadTimeConfig, ...others } = formState.value;
    const { data } = await saveMaterialListing({
      variables: {
        input: {
          ...others,
          variants: formState.variants.value?.map((mv) => ({
            ...omit(mv, "code"),
            images: mv.images?.map((im) => omit(im, "downloadUrl", "attachmentUrl")),
            components: mv.components?.map(({ componentId, componentItemId, componentValues, ...otherOpts }) => ({
              ...otherOpts,
              ...(componentItemId ? { componentItemId, componentValues } : { componentId }), // Take out the component Id since it references the placeholder used as base for the new MV
            })),
          })),
          ...maybeOptionPricingConfig(optionPricingConfig),
          ...maybeMaterialLeadTimeConfig(materialLeadTimeConfig),
        },
      },
      refetchQueries: [MaterialCatalogDocument],
    });

    const savedMaterial = data?.saveMaterialListing;
    if (savedMaterial) {
      triggerNotice({
        message: `Materials saved successfully`,
        icon: "success",
      });
      history.push(createMaterialDetailsUrl(savedMaterial.materialVariants.first!.id));
    }
  };

  return (
    <div>
      <PageHeader title="Add a New Material" backButton={createMaterialCatalogUrl()} />
      <div css={Css.df.jcc.$}>
        <div css={Css.w100.maxwPx(1600).h("calc(100vh - 260px)").oya.bgWhite.py2.px4.$}>
          <MaterialListingForm
            isNew={true}
            formState={formState}
            canEditItem={undefined}
            items={items}
            brands={brands}
            isOptionCatalogV2Enabled={isOptionCatalogV2Enabled}
            markets={markets}
            tags={tags}
          />
        </div>
      </div>
      <footer
        css={Css.df.fixed.z999.bottom0.left0.right0.bgGray100.p3.boxShadow("0px 0px 32px rgba(201, 201, 201, 0.75)").$}
      >
        <div css={Css.mla.$}>
          <SubmitButton form={formState} label="Add Material" onClick={handleSave} size="lg" />
        </div>
      </footer>
    </div>
  );
}
